import { sha256 } from 'js-sha256';
import type { FirstResultType } from '$lib/apis/content-factory/onboarding';

export type FirstResultSelectionIntent = {
	state_id: string;
	first_result_type: FirstResultType;
	custom_request?: string;
};

export type FirstResultSelectionSubmission = FirstResultSelectionIntent & {
	idempotency_key: string;
	expected_revision?: number;
};

export type FirstResultSelectionStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

type StoredSubmission = {
	version: 1;
	replay_scope: string;
	intent: FirstResultSelectionIntent;
	submission: FirstResultSelectionSubmission;
	response_received: boolean;
	response_revision: number | null;
};

const STORAGE_PREFIX = 'aeoll:onboarding:first-result-selection:v1:';

export const ONBOARDING_FIRST_RESULT_REVISION_CONFLICT_MESSAGE =
	'Данные онбординга изменились в другой вкладке. Я обновил состояние. Проверьте выбор и отправьте запрос ещё раз.';

export const normalizeOnboardingRevision = (value: unknown): number | null => {
	return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : null;
};

export const buildOnboardingFirstResultSelectionIdempotencyKey = (
	intent: FirstResultSelectionIntent,
	snapshotRevision: number | null | undefined,
	operationLabel = 'selection'
) => {
	const statePart = encodeURIComponent(intent.state_id.trim() || 'onboarding');
	const digest = sha256(
		JSON.stringify({
			version: 1,
			operation: operationLabel,
			intent,
			expected_revision: normalizeOnboardingRevision(snapshotRevision)
		})
	).slice(0, 40);
	return `onboarding-first-result-${statePart}-${digest}`;
};

const intentFor = (submission: FirstResultSelectionSubmission): FirstResultSelectionIntent => ({
	state_id: submission.state_id,
	first_result_type: submission.first_result_type,
	...(submission.custom_request !== undefined ? { custom_request: submission.custom_request } : {})
});

const sameIntent = (left: FirstResultSelectionIntent, right: FirstResultSelectionIntent) =>
	left.state_id === right.state_id &&
	left.first_result_type === right.first_result_type &&
	left.custom_request === right.custom_request;

const storageKeyFor = (intent: FirstResultSelectionIntent, replayScope: string) =>
	`${STORAGE_PREFIX}${encodeURIComponent(intent.state_id)}:${encodeURIComponent(replayScope)}:${sha256(JSON.stringify(intent))}`;

const parseStoredSubmission = (raw: string | null): StoredSubmission | null => {
	if (!raw) return null;
	try {
		const value = JSON.parse(raw) as Partial<StoredSubmission>;
		const submission = value.submission as FirstResultSelectionSubmission | undefined;
		const intent = value.intent as FirstResultSelectionIntent | undefined;
		if (
			value.version !== 1 ||
			typeof value.replay_scope !== 'string' ||
			!intent ||
			!submission ||
			typeof intent.state_id !== 'string' ||
			typeof intent.first_result_type !== 'string' ||
			typeof submission.idempotency_key !== 'string' ||
			!sameIntent(intent, intentFor(submission))
		) {
			return null;
		}
		return {
			version: 1,
			replay_scope: value.replay_scope,
			intent,
			submission,
			response_received: value.response_received === true,
			response_revision: normalizeOnboardingRevision(value.response_revision)
		};
	} catch {
		return null;
	}
};

const readStoredSubmission = (
	storage: FirstResultSelectionStorage | null | undefined,
	key: string
): StoredSubmission | null => {
	if (!storage) return null;
	const raw = storage.getItem(key);
	const parsed = parseStoredSubmission(raw);
	if (raw && !parsed) storage.removeItem(key);
	return parsed;
};

const shouldReuseStoredSubmission = (
	stored: StoredSubmission,
	intent: FirstResultSelectionIntent,
	snapshotRevision: number | null,
	idempotencyKey: string
) => {
	if (!sameIntent(stored.intent, intent)) return false;
	if (!stored.response_received) return true;
	if (stored.response_revision === null || snapshotRevision === null) {
		return stored.submission.idempotency_key === idempotencyKey;
	}
	return snapshotRevision <= stored.response_revision;
};

const writeStoredSubmission = (
	storage: FirstResultSelectionStorage | null | undefined,
	key: string,
	stored: StoredSubmission
) => storage?.setItem(key, JSON.stringify(stored));

const updateMatchingStoredSubmission = (
	storage: FirstResultSelectionStorage | null | undefined,
	key: string,
	stored: StoredSubmission
) => {
	if (!storage) return;
	const current = readStoredSubmission(storage, key);
	if (current?.submission.idempotency_key === stored.submission.idempotency_key) {
		writeStoredSubmission(storage, key, stored);
	}
};

const removeMatchingStoredSubmission = (
	storage: FirstResultSelectionStorage | null | undefined,
	key: string,
	submission: FirstResultSelectionSubmission
) => {
	if (!storage) return;
	const current = readStoredSubmission(storage, key);
	if (current?.submission.idempotency_key === submission.idempotency_key) {
		storage.removeItem(key);
	}
};

export const isOnboardingFirstResultRevisionConflict = (error: unknown): boolean => {
	if (!error || typeof error !== 'object') return false;
	const candidate = error as { status?: unknown; code?: unknown };
	return candidate.status === 409 && candidate.code === 'invalid_transition';
};

/**
 * Persist the exact selection envelope before dispatch so a timeout/reload can
 * replay the same key, body, and UI-captured revision. This helper never reads
 * server state and never retries a failed request itself.
 */
export const submitOnboardingFirstResultSelection = async <
	Response extends { revision?: unknown }
>({
	storage,
	intent,
	snapshotRevision,
	idempotencyKey,
	replayScope = 'default',
	send
}: {
	storage?: FirstResultSelectionStorage | null;
	intent: FirstResultSelectionIntent;
	snapshotRevision: number | null | undefined;
	idempotencyKey: string;
	replayScope?: string;
	send: (submission: FirstResultSelectionSubmission) => Promise<Response>;
}): Promise<Response> => {
	const normalizedSnapshotRevision = normalizeOnboardingRevision(snapshotRevision);
	const normalizedReplayScope = replayScope.trim() || 'default';
	const key = storageKeyFor(intent, normalizedReplayScope);
	const existing = readStoredSubmission(storage, key);
	let stored: StoredSubmission;
	if (
		existing &&
		existing.replay_scope === normalizedReplayScope &&
		shouldReuseStoredSubmission(existing, intent, normalizedSnapshotRevision, idempotencyKey)
	) {
		stored = existing;
	} else {
		const submission: FirstResultSelectionSubmission = {
			...intent,
			...(normalizedSnapshotRevision !== null
				? { expected_revision: normalizedSnapshotRevision }
				: {}),
			idempotency_key: idempotencyKey
		};
		stored = {
			version: 1,
			replay_scope: normalizedReplayScope,
			intent: intentFor(submission),
			submission,
			response_received: false,
			response_revision: null
		};
		writeStoredSubmission(storage, key, stored);
	}

	try {
		const response = await send(stored.submission);
		const responseRevision = normalizeOnboardingRevision(response?.revision);
		updateMatchingStoredSubmission(storage, key, {
			...stored,
			response_received: true,
			response_revision: responseRevision
		});
		return response;
	} catch (error) {
		if (isOnboardingFirstResultRevisionConflict(error)) {
			removeMatchingStoredSubmission(storage, key, stored.submission);
		}
		throw error;
	}
};
